import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PenSquare, RefreshCw, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useCombinedFeed, { FEED_TYPES } from '../../hooks/notifications/useCombinedFeed';
import { selectFeedRefreshTrigger } from '../../slices/notificationsSlice';
import { selectBlockedUserIds } from '../../slices/blocksSlice';
import FeedFilterBar from './FeedFilterBar';
import FeedCard from './FeedCard';
import FeedEmptyState from './FeedEmptyState';
import { FeedSkeletonGrid } from './FeedCardSkeleton';
import EditPostModal from '../EditPostModal/EditPostModal';
import ExtendPostModal from '../ExtendPostModal/ExtendPostModal';
import { Button } from '../ui/button';

/**
 * Main container component for the notification/community feed
 * Now with infinite scroll pagination support
 */
const FeedContainer = ({ 
  className = '',
  showHeader = true,
  onCreatePost,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState(FEED_TYPES.ALL);

  // Edit modal state
  const [editingPost, setEditingPost] = useState(null);

  const [extendingPost, setExtendingPost] = useState(null);

  // Ref for infinite scroll observer
  const loadMoreRef = useRef(null);

  // Fetch combined feed data with pagination
  const {
    feed,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore,
    loadMore,
    counts,
    refetch,
    hasCoordinates,
  } = useCombinedFeed({ filter: activeFilter });

  const blockedIds = useSelector(selectBlockedUserIds);
  const visibleFeed = useMemo(
    () =>
      feed.filter((item) => {
        if (!item.userId) return true;
        return !blockedIds.includes(item.userId);
      }),
    [feed, blockedIds]
  );

  // Listen for feed refresh trigger
  const feedRefreshTrigger = useSelector(selectFeedRefreshTrigger);
  const prevTriggerRef = useRef(feedRefreshTrigger);

  useEffect(() => {
    if (feedRefreshTrigger && feedRefreshTrigger !== prevTriggerRef.current) {
      prevTriggerRef.current = feedRefreshTrigger;
      refetch();
    }
  }, [feedRefreshTrigger, refetch]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoadingMore, isLoading, loadMore]);

  // Handle filter change
  const handleFilterChange = useCallback((newFilter) => {
    setActiveFilter(newFilter);
  }, []);

  // Handle create post click
  const handleCreatePost = useCallback((postType) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (onCreatePost) {
      onCreatePost(postType);
    } else {
      navigate('/notifications/create-post', { state: { type: postType } });
    }
  }, [isAuthenticated, navigate, onCreatePost]);

  // Handle contact click (for unauthenticated users)
  const handleContactClick = useCallback((item) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  const handleEditPost = useCallback((item) => {
    const postData = {
      _id: item.id,
      id: item.id,
      title: item.title,
      description: item.description,
      animalName: item.animalName,
      animalType: item.animalType,
      breed: item.breed,
      colorMarkings: item.colorMarkings,
      sex: item.sex,
      estimatedWeight: item.estimatedWeight,
      approachable: item.approachable,
      lastSeen: item.lastSeen,
      foundNear: item.foundNear,
      rewardAmount: item.rewardAmount,
      contactName: item.contactName,
      contactPhone: item.contactPhone,
      contactEmail: item.contactEmail,
      imageUrl: item.imageUrl,
    };
    setEditingPost({ data: postData, type: item.type });
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditingPost(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditingPost(null);
    refetch();
  }, [refetch]);

  const handleExtendClick = useCallback((item) => {
    if (!item) return;
    setExtendingPost({
      _id: item.id,
      postType: item.type,
      expiresAt: item.expiresAt,
      lastExtendedAt: item.lastExtendedAt,
      extensionCount: item.extensionCount,
      title: item.title,
      animalName: item.animalName,
    });
  }, []);

  const handleExtendModalClose = useCallback(() => {
    setExtendingPost(null);
  }, []);

  // Animation variants for staggered reveal
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.25,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className={`bg-gray-50 backdrop-blur-md rounded-b-xl sm:rounded-b-2xl overflow-hidden ${className}`}>
      <div className="bg-sidebar">
        {/* Feed Header */}
        {showHeader && (
          <div className="px-3 sm:px-4 pt-3 sm:pt-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm sm:text-base md:text-lg font-bold text-foreground">
                {t('community_feed', 'Local Updates')}
              </h2>
              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* Refresh button */}
                <button
                  onClick={refetch}
                  disabled={isLoading}
                  className="p-1.5 sm:p-2 hover:bg-white/50 rounded-lg transition-colors disabled:opacity-50 min-w-[36px] min-h-[36px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
                  aria-label={t('refresh', 'Refresh')}
                >
                  <RefreshCw 
                    className={`w-4 h-4 text-muted-foreground ${isLoadingMore ? 'animate-spin' : ''}`} 
                  />
                </button>
                
                {/* Create Post Button */}
                <Button
                  onClick={() => handleCreatePost(null)}
                  size="sm"
                  className="gap-1.5 sm:gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs sm:text-sm px-3 sm:px-4 rounded-full"
                >
                  <PenSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>{t('create_post', 'Create Post')}</span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <FeedFilterBar
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          counts={counts}
        />
      </div>

      {/* Feed Content */}
      <div className="p-3 sm:p-4 min-h-[300px] bg-gray-50">
        {/* Loading State */}
        {isLoading && (
          <FeedSkeletonGrid count={3} />
        )}

        {/* Error or Empty State */}
        {!isLoading && (visibleFeed.length === 0 || hasError) && (
          <FeedEmptyState
            filter={activeFilter}
            hasCoordinates={hasCoordinates}
            hasError={hasError}
            onCreatePost={handleCreatePost}
            onRetry={refetch}
          />
        )}

        {/* Feed Items */}
        {!isLoading && visibleFeed.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-4 max-w-2xl mx-auto"
            >
              {visibleFeed.map((item) => (
                <motion.div
                  key={item.id}
                  variants={itemVariants}
                >
                  <FeedCard
                    item={item}
                    onContactClick={handleContactClick}
                    onEditPost={handleEditPost}
                    onExtendClick={handleExtendClick}
                    showOwnerLifecycle={activeFilter === FEED_TYPES.MY_POSTS}
                  />
                </motion.div>
              ))}

              {/* Infinite Scroll Trigger */}
              <div 
                ref={loadMoreRef} 
                className="h-4"
                aria-hidden="true"
              />

              {/* Loading More Indicator */}
              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-gray-50 px-4 py-2 rounded-full">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('loading_more', 'Loading more...')}
                  </div>
                </div>
              )}

              {/* End of Feed Message */}
              {!hasMore && visibleFeed.length >= 5 && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">
                    {t('end_of_feed', "You've reached the end of the feed")}
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Edit Post Modal */}
      <EditPostModal
        isOpen={!!editingPost}
        onClose={handleEditModalClose}
        postData={editingPost?.data}
        postType={editingPost?.type}
        onSuccess={handleEditSuccess}
      />

      <ExtendPostModal
        isOpen={!!extendingPost}
        post={extendingPost}
        onClose={handleExtendModalClose}
        onExtended={() => refetch()}
      />
    </div>
  );
};

export default FeedContainer;
